import { CommentCategory } from 'annotator/annotator-types';
import React, { createContext, useEffect, useReducer } from 'react';

type Action = { type: string; payload: string };

export const AnnotatorSidebarContext: React.Context<string> = createContext<string>('All');
export const AnnotatorSidebarDispatchContext: React.Context<React.Dispatch<Action> | null> = createContext(null);

// Convert category value from URL (snake_case) to enum key (PascalCase)
// E.g., "use_of_sources" -> "UseOfSources"
const getCategoryKeyFromValue = (categoryValue: string): string => {
  const normalizedValue = categoryValue.trim().toLowerCase();

  // Find the enum key whose value matches the URL param
  const categoryKey = Object.keys(CommentCategory).find(
    (key) => CommentCategory[key as keyof typeof CommentCategory].toLowerCase() === normalizedValue
  );

  return categoryKey || 'All';
};

export const AnnotatorSidebarProvider = ({ children }): React.JSX.Element => {
  const [active, dispatch] = useReducer(activeTabReducer, 'All');

  // Load initial category from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get('category');

    if (categoryParam) {
      const categoryKey = getCategoryKeyFromValue(categoryParam);
      dispatch({ type: 'set', payload: categoryKey });
    }
  }, []);

  // Update URL when active category changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryValue = CommentCategory[active as keyof typeof CommentCategory];

    if (categoryValue) {
      params.set('category', categoryValue);

      const newSearch = params.toString();
      const currentSearch = window.location.search.replace(/^\?/, '');

      if (currentSearch !== newSearch) {
        const url = `${window.location.pathname}?${newSearch}${window.location.hash}`;
        window.history.replaceState(null, '', url);
      }
    }
  }, [active]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const categoryParam = params.get('category');

      if (categoryParam) {
        const categoryKey = getCategoryKeyFromValue(categoryParam);
        dispatch({ type: 'set', payload: categoryKey });
      } else {
        dispatch({ type: 'set', payload: 'All' });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <AnnotatorSidebarContext.Provider value={active}>
      <AnnotatorSidebarDispatchContext.Provider value={dispatch}>
        {children}
      </AnnotatorSidebarDispatchContext.Provider>
    </AnnotatorSidebarContext.Provider>
  );
};

// Reducer for active tab
const activeTabReducer = (active: string, action): string => {
  // Set the active tab
  switch (action.type) {
    case 'set':
      return action.payload;
    default:
      return active;
  }
};
